// src/components/MAP/hooks/useMapInitialization.js
import { useState, useRef, useEffect } from 'react';
import L from 'leaflet';
import { CustomMarkers } from '../customMarkers';
import { RouteIconsManager } from '../routeIconsManager';
import { calculateBoundsWithBuffer, processCSVData, createRouteVisualization } from '../utils/mapUtils';
import { loadIconPositions } from '../loadIconPositions';

export const useMapInitialization = (mapElementId, csvFiles, timeframe, activeRobotId = null) => {

  const [routes, setRoutes] = useState({});
  const [timelineData, setTimelineData] = useState([]);
  const [currentVisibleIcons, setCurrentVisibleIcons] = useState([]);
  
  const mapInstance = useRef(null);
  const polylinesRef = useRef({});
  const backgroundPolylinesRef = useRef({});
  const markersRef = useRef({});
  const robotPositionUpdatersRef = useRef({});
  const customMarkersRef = useRef(null);
  const routeIconsManager = useRef(new RouteIconsManager());
  const isMapMounted = useRef(true);
  
  // 新增：用于节流机器人位置更新
  const lastPositionUpdateTimeRef = useRef(0);
  const POSITION_UPDATE_THROTTLE_MS = 100;
  
  const DEFAULT_MAP_CENTER = [38.8291, -77.308435];
  const DEFAULT_ZOOM = 13;
  const TRAJECTORY_COLOR = "#034ec9";
  
  // 清理地图函数
  const clearMap = () => {
    // 清除现有图层
    Object.values(polylinesRef.current).forEach((polyline) => {
      if (mapInstance.current && polyline) {
        polyline.remove();
      }
    });

    Object.values(backgroundPolylinesRef.current).forEach((polyline) => {
      if (mapInstance.current && polyline) {
        polyline.remove();
      }
    });

    Object.values(markersRef.current).forEach((marker) => {
      if (mapInstance.current && marker) {
        marker.remove();
      }
    });

    // 重置所有引用和状态
    polylinesRef.current = {};
    backgroundPolylinesRef.current = {};
    markersRef.current = {};
    robotPositionUpdatersRef.current = {}; // 确保清除机器人位置更新器
    
    // 清除自定义标记
    if (customMarkersRef.current) {
      customMarkersRef.current.clear();
    }
    
    // 清除路由图标管理器
    if (routeIconsManager.current) {
      routeIconsManager.current.clearAll();
    }
    
    // 重置状态
    setTimelineData([]);
    setRoutes({});
    setCurrentVisibleIcons([]);
  };

  const rebuildMap = () => {
    // 先销毁现有地图
    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }
    
    // 清空所有引用
    polylinesRef.current = {};
    backgroundPolylinesRef.current = {};
    markersRef.current = {};
    robotPositionUpdatersRef.current = {};
    
    // 清除自定义标记
    if (customMarkersRef.current) {
      customMarkersRef.current.clear();
      customMarkersRef.current = null;
    }
    
    // 清空路由管理器
    routeIconsManager.current.clearAll();
    
    // 重置状态
    setTimelineData([]);
    setRoutes({});
    setCurrentVisibleIcons([]);
    
    // 创建新地图
    const mapElement = document.getElementById(mapElementId);
    if (mapElement) {
      mapInstance.current = L.map(mapElementId, {
        maxBoundsViscosity: 1.0,
        bounceAtZoomLimits: false,
        inertia: false,
      }).setView(DEFAULT_MAP_CENTER, DEFAULT_ZOOM);
      
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        }
      ).addTo(mapInstance.current);
      
      customMarkersRef.current = new CustomMarkers(mapInstance.current, []);
    }
  };
  
  // 处理挂载状态
  useEffect(() => {
    isMapMounted.current = true;
    
    return () => {
      isMapMounted.current = false;
    };
  }, []);
  
  // 初始化地图
  useEffect(() => {
    // 如果地图还没初始化，先初始化
    if (!mapInstance.current) {
      console.log(`Initializing map with ID: ${mapElementId}`);
      
      // 确保DOM元素存在
      const mapElement = document.getElementById(mapElementId);
      if (!mapElement) {
        console.error(`Map element with ID ${mapElementId} not found!`);
        return;
      }
      
      mapInstance.current = L.map(mapElementId, {
        maxBoundsViscosity: 1.0,
        bounceAtZoomLimits: false,
        inertia: false,
      }).setView(DEFAULT_MAP_CENTER, DEFAULT_ZOOM);
      
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        }
      ).addTo(mapInstance.current);
      
      customMarkersRef.current = new CustomMarkers(mapInstance.current, []);
    }

    // 每次CSV文件或时间框架变化时，重新加载路线和图标
    clearMap();
    setTimelineData([]);
    setRoutes({});
    routeIconsManager.current.clearAll();

    const loadRoutesAndIcons = async () => {
      let allIcons = [];
      let allRoutePoints = [];

      for (let index = 0; index < csvFiles.length; index++) {
        const shouldPrioritize = activeRobotId && (index + 1) === activeRobotId;
        
        if (shouldPrioritize) {
          console.log(`Prioritizing loading for active robot ID: ${activeRobotId}`);
          // Add code to ensure this robot's route gets loaded first
        }
        const csvFile = csvFiles[index];
        try {
          const response = await fetch(csvFile);
          const data = await response.text();
          if (data.startsWith("<") || data.includes("<html")) {
            throw new Error("Invalid CSV format");
          }

          const { routeLatLngs, timeOffsets } = processCSVData(data);
          if (routeLatLngs.length > 1) {
            allRoutePoints = [...allRoutePoints, ...routeLatLngs];
          
            // 使用共享函数创建轨迹
            const { dynamicPolyline, backgroundPolyline, marker, updateRobotPosition } = createRouteVisualization(
              mapInstance.current, 
              routeLatLngs, 
              index,
              {
                trajectoryColor: TRAJECTORY_COLOR,
                timeframe: timeframe,
                timeOffsets: timeOffsets,
                initiallyHideMarker: true, // Add this parameter
                onTrajectoryClick: (idx) => {
                  const robotId = idx + 1;
                  const timePart = timeframe === "11AM" ? "day" : "night";
                  const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
                  
                  console.log(`Map trajectory clicked: robot ${robotId}`);
                },
                onMarkerClick: (idx) => {
                  const robotId = idx + 1;
                  const timePart = timeframe === "11AM" ? "day" : "night";
                  const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
                  
                  console.log(`Map marker clicked: robot ${robotId}`);
                }
              }
            );
            
            // 保存引用
            backgroundPolylinesRef.current[index] = backgroundPolyline;
            polylinesRef.current[index] = dynamicPolyline;
            markersRef.current[index] = marker;
            robotPositionUpdatersRef.current[index] = updateRobotPosition;
          
            const iconsCsvPath = `/icons_route${index + 1}.csv`;
            try {
              const icons = await loadIconPositions(iconsCsvPath, index, timeframe);
              routeIconsManager.current.registerIcons(index, icons);
          
              icons.forEach((icon) => {
                if (icon.position) {
                  allRoutePoints.push(icon.position);
                }
              });
          
              allIcons = [...allIcons, ...icons];
            } catch (error) {
              console.error(`Error loading icons for route ${index + 1}:`, error);
            }
          
            setRoutes((prev) => ({
              ...prev,
              [index]: {
                visible: true,
                color: TRAJECTORY_COLOR,
                timelineVisible: true,
              },
            }));
          
            setTimelineData((prev) => [
              ...prev,
              { index, routeLatLngs, timeOffsets },
            ]);
          }
        } catch (error) {
          console.error(`Error processing ${csvFile}:`, error);
        }
      }

      if (allRoutePoints.length > 0 && isMapMounted.current) {
        const bufferedBounds = calculateBoundsWithBuffer(allRoutePoints, 0.01);
        if (bufferedBounds && mapInstance.current) {
          mapInstance.current.fitBounds(bufferedBounds, {
            padding: [2, 2],
            maxZoom: 18,
          });
          mapInstance.current.setMaxBounds(bufferedBounds);
          const currentZoom = mapInstance.current.getZoom();
          mapInstance.current.setMinZoom(currentZoom);
          mapInstance.current.setMaxZoom(19);
        }
      }

      if (isMapMounted.current) {
        setCurrentVisibleIcons(allIcons);
        if (customMarkersRef.current) {
          customMarkersRef.current.clear();
          customMarkersRef.current.addMarkers(allIcons);
        }
      }
    };

    loadRoutesAndIcons();

    // 清理函数
    return () => {
      clearMap();
      // 不要在这里移除地图实例，那应该在组件完全卸载时处理
    };
  }, [csvFiles, timeframe, mapElementId, activeRobotId]);

  // 组件卸载时清理地图实例
  useEffect(() => {
    return () => {
      clearMap();
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);
  
  // 优化更新机器人位置的方法
// 在 useMapInitialization.js 中修改 updateRobotPositions 函数
const updateRobotPositions = (time, staticUpdate = false) => {
  if (!mapInstance.current || !robotPositionUpdatersRef.current) return;
  
  // 确保时间有效
  if (typeof time !== 'number' || isNaN(time) || time < 0) return;
  
  // 在静态更新模式下，临时禁用地图的自动平移功能
  if (staticUpdate && mapInstance.current) {
    // 保存当前设置
    const originalPanningEnabled = mapInstance.current.options.keyboard;
    const originalScrollWheelZoom = mapInstance.current.options.scrollWheelZoom;
    
    // 临时禁用
    mapInstance.current.keyboard.disable();
    mapInstance.current.scrollWheelZoom.disable();
    
    // 处理更新
    Object.entries(robotPositionUpdatersRef.current).forEach(([index, updatePosition]) => {
      if (updatePosition && typeof updatePosition === 'function') {
        try {
          // 调用更新但不触发自动平移
          updatePosition(time, { noPan: true });
        } catch (error) {
          console.error(`Error updating robot position for index ${index}:`, error);
        }
      }
    });
    
    // 恢复设置
    if (originalPanningEnabled) mapInstance.current.keyboard.enable();
    if (originalScrollWheelZoom) mapInstance.current.scrollWheelZoom.enable();
  } else {
    // 常规更新
    Object.entries(robotPositionUpdatersRef.current).forEach(([index, updatePosition]) => {
      if (updatePosition && typeof updatePosition === 'function') {
        try {
          updatePosition(time);
        } catch (error) {
          console.error(`Error updating robot position for index ${index}:`, error);
        }
      }
    });
  }
};

  return {
    mapInstance,
    routes,
    setRoutes,
    timelineData,
    setTimelineData,
    polylinesRef,
    backgroundPolylinesRef, 
    markersRef,
    customMarkersRef,
    routeIconsManager,
    currentVisibleIcons,
    setCurrentVisibleIcons,
    clearMap,
    rebuildMap,
    updateRobotPositions,
    robotPositionUpdatersRef
  };
};